import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase, logSupabaseEvent } from '@/integrations/supabase/client';
import meta from '@/data/generated/meta.json';

interface HybridConfig<T> {
  resourceName: string;
  staticData: T[];
  fetchDynamic: (lastBuildDate: string) => Promise<T[] | null>;
  queryKey: string[];
  sortFn?: (a: T, b: T) => number;
  skipUpdates?: boolean;
}

export function useHybridData<T extends { id: string | number }>({
  resourceName,
  staticData,
  fetchDynamic,
  queryKey,
  sortFn,
  skipUpdates = false,
}: HybridConfig<T>) {
  // 1. Initialize with Static Data (instant)
  const [data, setData] = useState<T[]>(staticData);
  const [isStatic, setIsStatic] = useState(true);



  const { data: dynamicData, isLoading: isCheckingUpdates, error } = useQuery({
    queryKey: [...queryKey, 'hybrid-update', meta.lastBuildDate],
    queryFn: async () => {
      if (skipUpdates) {
        return [];
      }
      // Respect "stale enough" check
      const buildTime = new Date(meta.lastBuildDate).getTime();
      const now = new Date().getTime();
      // If build is less than 5 minutes old, assume static data is fresh enough
      if ((now - buildTime) < 1000 * 60 * 5) {
         // logSupabaseEvent('Info', `Static data is fresh (${Math.round((now - buildTime) / 1000)}s old). Skipping Supabase query for ${resourceName}.`);
         return [];
      }

      logSupabaseEvent('Query', `Checking for new ${resourceName} since ${meta.lastBuildDate}`);
      const newData = await fetchDynamic(meta.lastBuildDate);
      return newData || [];
    },
    // Don't refetch too often
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // 3. Merge Strategy
  useEffect(() => {
    if (dynamicData && dynamicData.length > 0) {
      setData(currentStatic => {
        // Create a map of existing IDs for de-duplication
        const existingIds = new Set(currentStatic.map(item => item.id));
        
        // Filter out duplicates
        const uniqueNewItems = dynamicData.filter(item => !existingIds.has(item.id));
        
        if (uniqueNewItems.length === 0) {
           // Data was fetched but nothing new.
           // Only log if we haven't already verified this to avoid spamming on re-renders,
           // but since dynamicData ref changes, it's okay to log once per fetch.
           // However, to keep it clean, we can skip logging here or use a debug log.
           return currentStatic;
        }

        logSupabaseEvent('Update', `Found ${uniqueNewItems.length} new ${resourceName} items. Merging...`);
        
        // Combine and sort
        const combined = [...uniqueNewItems, ...currentStatic];
        return sortFn ? combined.sort(sortFn) : combined;
      });
      setIsStatic(false);
    } else if (dynamicData) {
       // dynamicData is empty array (handled in queryFn or returned empty)
       // We only log if it was an actual query that returned empty, 
       // but our queryFn returns empty array if skipped too.
       // So we can optionally log here if needed, or rely on the queryFn log.
       // logSupabaseEvent('Info', `No new ${resourceName} found. Static data is up to date.`);
    }
  }, [dynamicData, resourceName, sortFn]);

  return {
    data,
    isStatic,
    isCheckingUpdates,
    error
  };
}
